/**
 * useReferralTracking - Captures referral codes from URL params and stores them as cookies
 *
 * On mount, checks the current URL for `?ref=CODE` or `?partner=CODE`. If found,
 * stores the referral code along with the landing page and UTM params in a 30-day cookie.
 *
 * Exports helper functions for reading and clearing the stored referral data.
 *
 * Usage:
 *   // Mount this hook once at the app root (e.g., in RootLayout)
 *   useReferralTracking();
 *
 *   // Later, read the stored data during signup:
 *   import { getReferralData, clearReferralData } from '@/hooks/useReferralTracking';
 *   const referral = getReferralData();
 */

import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import {
  parseReferralFromUrl,
  setReferralCookie,
  getReferralCookie,
  clearReferralCookie,
} from '@/lib/referral/utils';
import type { ReferralCookieData } from '@/lib/referral/utils';

/**
 * Read the currently stored referral data from the cookie.
 * Safe to call outside of React components.
 */
export function getReferralData(): ReferralCookieData | null {
  return getReferralCookie();
}

/**
 * Clear the stored referral data. Call this after successful signup attribution.
 * Safe to call outside of React components.
 */
export function clearReferralData(): void {
  clearReferralCookie();
}

/**
 * Hook that captures referral parameters from the URL on mount.
 *
 * If a referral cookie already exists, it is NOT overwritten (first-touch attribution).
 * This ensures the original referrer gets credit even if the user later visits
 * via a different referral link.
 */
export function useReferralTracking(): void {
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;

      // Check if we already have a referral cookie (first-touch attribution)
      const existing = getReferralCookie();
      if (existing) {
        logger.log('[referral] Existing referral cookie found, skipping capture:', existing.referral_code);
        return;
      }

      // Parse referral data from the current URL
      const referralData = parseReferralFromUrl(window.location.href);
      if (!referralData) return;

      // Store in cookie
      setReferralCookie(referralData);
      logger.log('[referral] Captured referral from URL:', referralData.referral_code);
    } catch {
      logger.warn('[referral] Error during referral tracking initialization');
    }
  }, []);
}
